import React, { useCallback, useState } from "react";
import { Search } from "react-feather";
import { rawColors } from "../../../design-system/tokens/colors";
import { MemberSearchDropdown } from "../../molecules/MemberSearchDropdown";
import { useMemberInvitePicker } from "../../../hooks/useMemberInvitePicker";
import { Globe, Lock, Briefcase } from "react-feather";
import { AccessPolicySelect } from "../../molecules/AccessPolicySelect";
import { DurationPicker } from "../../molecules/DurationPicker";
import { PreviewSplitLayout } from "../../molecules/PreviewSplitLayout";
import { LivePreviewFrame } from "../../molecules/LivePreviewFrame";
import { CopyUrlField } from "../../molecules/CopyUrlField";
import { embedPreviewSrc } from "../../../services/shareLinks/embedPreviewSrc";
import { EmbedSettingsPanel } from "../EmbedSettingsPanel";
import { ShareLinkActions } from "../ShareLinkActions";
import { useEmbedPreviewToken } from "../../../hooks/useEmbedPreviewToken";
import { useStoryShareLinks } from "../../../hooks/useStoryShareLinks";
import { SHARE_COPY as SC } from "./ShareStoryModal.copy";
import type { ShareLinkAccess, UpdateShareLinkRequest } from "../../../services/shareLinks/shareLinks.types";
import { useStoryInvitees } from "../../../hooks/useStoryInvitees";
import { useCreateStoryInvitees } from "../../../hooks/useCreateStoryInvitees";
import { getInviteKey } from "../../../services/workspaces/workspaceMembers.helpers";
import { getApiErrorMessage } from "../../../services/apiErrors";
import { shareLinkUrl } from "../../../services/shareLinks/shareLinkUrl";
import {
  mapMemberInvitesToInviteeRequests,
  mapStoryInviteesToCurrentMembers,
} from "../../../services/storyInvitees/storyInviteesMappers";
import type {
  ShareStoryModalProps,
  ShareStoryTab,
  ShareStoryValues,
  StoryCurrentMember,
} from "./ShareStoryModal.types";
import {
  ShareDialog,
  DialogHeader,
  DialogTitle,
  CloseButton,
  TabBar,
  TabButton,
  TabPanel,
  TabBody,
  MembersSearchBlock,
  InviteRow,
  InviteField,
  InviteInput,
  ClearButton,
  RolePill,
  PersonList,
  PersonRow,
  Avatar,
  PersonIdentity,
  PersonName,
  PersonMeta,
  OwnerLabel,
  RemoveButton,
  ApiErrorBanner,
  ContentBox,
  ContentText,
  PillButton,
  ChangeLink,
} from "./ShareStoryModal.styles";
import { WorkspaceInviteRoleSelect } from "../../molecules/WorkspaceInviteRoleSelect";
import { CloseIcon } from "../../atoms/CloseIcon";
import type {
  WorkspaceInviteRole,
  WorkspaceMemberInvite,
} from "../../../services/workspaces/workspaceMembers.types";

const SHARE_TABS: { id: ShareStoryTab; label: string }[] = [
  { id: "invite", label: "Invite" },
  { id: "copy-link", label: "Link" },
  { id: "embed", label: "Embed" },
];

const EMPTY_VALUES: ShareStoryValues = {
  memberInvites: [],
  currentMembers: [],
};

export const ShareStoryModal: React.FC<ShareStoryModalProps> = ({
  open = false,
  storyId,
  storyTitle = "this story",
  owner,
  initialTab = "invite",
  linkUrl,
  workspaceId,
  linkNote,
  embedUnavailableReason,
  onClose,
  onSuccess,
}) => {
  const [values, setValues] = useState<ShareStoryValues>(EMPTY_VALUES);
  const [apiError, setApiError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ShareStoryTab>(initialTab);
  const [copied, setCopied] = useState(false);

  const embedBlocked = Boolean(embedUnavailableReason);
  const shareLinks = useStoryShareLinks(storyId, open && !embedBlocked);
  const linkRow = shareLinks.query.data?.link;
  const embedRow = shareLinks.query.data?.embed;
  const previewToken = useEmbedPreviewToken(storyId, open && !embedBlocked && Boolean(embedRow));
  const invitees = useStoryInvitees(storyId, open);
  const createInvitees = useCreateStoryInvitees({
    onError: (err) =>
      setApiError(getApiErrorMessage(err, "Unable to send invites.")),
  });
  const isLoading = createInvitees.isPending;

  React.useEffect(() => {
    if (open && storyId) {
      setValues(EMPTY_VALUES);
      setApiError(null);
      setCopied(false);
      setActiveTab(initialTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, storyId]);

  // Seeds/refreshes currentMembers from the invite-lifecycle list — runs again
  // after a successful invite, since useCreateStoryInvitees invalidates this query.
  React.useEffect(() => {
    if (open && invitees.data) {
      setValues((prev) => ({
        ...prev,
        currentMembers: mapStoryInviteesToCurrentMembers(invitees.data),
      }));
    }
  }, [open, invitees.data]);

  React.useEffect(() => {
    if (open && shareLinks.query.isError) {
      setApiError(getApiErrorMessage(shareLinks.query.error, SC.loadError));
    }
  }, [open, shareLinks.query.isError, shareLinks.query.error]);

  React.useEffect(() => {
    if (open && invitees.isError) {
      setApiError(
        getApiErrorMessage(invitees.error, "Unable to load current members."),
      );
    }
  }, [open, invitees.isError, invitees.error]);

  const handleInvitesChange = useCallback(
    (memberInvites: ShareStoryValues["memberInvites"]) => {
      setValues((prev) => ({ ...prev, memberInvites }));
    },
    [],
  );

  const picker = useMemberInvitePicker({
    invites: values.memberInvites,
    onInvitesChange: handleInvitesChange,
    disabled: isLoading,
    searchScope: "tenant",
    initialRole: "viewer",
  });

  const handleCurrentMemberRoleChange = useCallback(
    (id: string, role: WorkspaceInviteRole) => {
      setValues((prev) => ({
        ...prev,
        currentMembers: prev.currentMembers.map((m) =>
          m.id === id ? { ...m, role } : m,
        ),
      }));
    },
    [],
  );

  const handleCurrentMemberRemove = useCallback((id: string) => {
    setValues((prev) => ({
      ...prev,
      currentMembers: prev.currentMembers.filter((m) => m.id !== id),
    }));
  }, []);

  const handleInvite = useCallback(() => {
    if (values.memberInvites.length === 0) return;
    createInvitees.mutate(
      {
        storyId,
        invitees: mapMemberInvitesToInviteeRequests(values.memberInvites),
      },
      {
        onSuccess: (response) => {
          const failed = response.results.filter((r) => r.status === "failed");
          setApiError(
            failed.length > 0
              ? (failed[0].error_message ??
                  `Unable to invite ${failed.length} ${
                    failed.length === 1 ? "person" : "people"
                  }.`)
              : null,
          );
          // Server is the source of truth for the new rows (pending/accepted/
          // already_member) — the invitees query refetch (invalidated by the
          // mutation) repopulates currentMembers; this only clears the draft.
          setValues((prev) => ({ ...prev, memberInvites: [] }));
          onSuccess?.();
        },
      },
    );
  }, [createInvitees, onSuccess, storyId, values.memberInvites]);

  const publicUrl = linkRow?.slug ? `${window.location.origin}/s/${linkRow.slug}` : null;
  const shareUrl = shareLinkUrl(window.location.origin, {
    slug: linkRow?.slug,
    linkUrl,
    workspaceId,
    storyId,
    currentHref: window.location.href,
  });

  const saveLink = useCallback(
    (kind: "link" | "embed", body: UpdateShareLinkRequest) => {
      setApiError(null);
      shareLinks.put.mutate(
        { kind, body },
        { onError: (err) => setApiError(getApiErrorMessage(err, SC.saveError)) },
      );
    },
    [shareLinks.put],
  );

  const handleReset = useCallback(
    (kind: "link" | "embed") => {
      setApiError(null);
      shareLinks.reset.mutate(kind, {
        onError: (err) => setApiError(getApiErrorMessage(err, SC.resetError)),
      });
    },
    [shareLinks.reset],
  );

  const handleCopy = useCallback(() => {
    const text = shareUrl;
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [shareUrl]);

  const titleId = "share-story-dialog-title";
  const preview = (
    <LivePreviewFrame
      heading={SC.previewHeading}
      title={SC.previewTitle}
      src={embedPreviewSrc(window.location.origin, previewToken.data, embedRow?.options)}
      emptyText={embedBlocked ? SC.previewUnavailable : SC.previewEmpty}
    />
  );

  return (
    <ShareDialog
      open={open}
      onClose={onClose}
      size="xl"
      closeOnBackdropClick={!isLoading}
      ariaLabelledBy={titleId}
    >
      <DialogHeader>
        <DialogTitle id={titleId}>Share “{storyTitle}”</DialogTitle>
        <CloseButton type="button" aria-label="Close" onClick={onClose}>
          <CloseIcon size={18} />
        </CloseButton>
      </DialogHeader>

      <TabBar role="tablist" aria-label="Share options">
        {SHARE_TABS.map((tab) => (
          <TabButton
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            $active={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </TabButton>
        ))}
      </TabBar>

      <TabBody>
      {/* One layout for Invite and Link, so the preview stays loaded when switching between them. */}
      {activeTab !== "embed" && (
        <PreviewSplitLayout preview={preview} controls={activeTab === "invite" ? (
        <TabPanel>
          <MembersSearchBlock
            ref={picker.anchorRef}
            data-testid="share-members-search-block"
          >
            <InviteRow data-testid="member-invite-search-bar">
              <InviteField>
                <Search
                  size={16}
                  color={rawColors.text.mediumEmphasis}
                  aria-hidden
                />
                <InviteInput
                  ref={picker.inputRef}
                  type="text"
                  value={picker.query}
                  placeholder="Add people by email"
                  disabled={isLoading}
                  onChange={(e) => picker.handleQueryChange(e.target.value)}
                  onFocus={picker.openDropdown}
                  onKeyDown={picker.handleInputKeyDown}
                  aria-label="Search members by name or email"
                  data-testid="member-invite-search-input"
                />
                {picker.query.length > 0 ? (
                  <ClearButton
                    type="button"
                    aria-label="Clear search"
                    disabled={isLoading}
                    onClick={picker.clearSearch}
                  >
                    <CloseIcon size={16} />
                  </ClearButton>
                ) : null}
              </InviteField>
              <RolePill $large>
                <WorkspaceInviteRoleSelect
                  value={picker.defaultRole}
                  onChange={picker.setDefaultRole}
                  disabled={isLoading}
                  ariaLabel="Default invite role"
                  presentation="chip"
                  chevronSize={14}
                />
              </RolePill>
              <PillButton
                variant="primary"
                size="md"
                loading={isLoading}
                disabled={isLoading || values.memberInvites.length === 0}
                onClick={handleInvite}
              >
                Invite
              </PillButton>
            </InviteRow>
            <MemberSearchDropdown
              open={picker.dropdownOpen}
              isLoading={picker.searchLoading}
              query={picker.query}
              results={picker.results}
              emailInviteCandidate={picker.emailInviteCandidate}
              menuRef={picker.menuRef}
              placement="inline"
              onSelectUser={picker.handleSelectUser}
              onSelectEmailInvite={picker.handleSelectEmailInvite}
            />
          </MembersSearchBlock>

          <PersonList data-testid="share-members-scroll-area">
            {owner ? (
            <PersonRow data-testid="story-share-owner-row">
              <Avatar $owner aria-hidden>
                {initialsOf(owner.name)}
              </Avatar>
              <PersonIdentity>
                <PersonName>{owner.name}</PersonName>
                {owner.designation ? (
                  <PersonMeta>{owner.designation}</PersonMeta>
                ) : null}
              </PersonIdentity>
              <OwnerLabel>Owner</OwnerLabel>
            </PersonRow>
            ) : null}
            {invitees.isLoading ? (
              <ContentText>Loading members…</ContentText>
            ) : null}
            {values.currentMembers.map((member) => (
              <CurrentMemberRow
                key={member.id}
                member={member}
                disabled={isLoading}
                onRoleChange={handleCurrentMemberRoleChange}
                onRemove={handleCurrentMemberRemove}
              />
            ))}
            {values.memberInvites.map((invite) => {
              const key = getInviteKey(invite);
              return (
                <DraftInviteRow
                  key={key}
                  invite={invite}
                  disabled={isLoading}
                  onRoleChange={(role) => picker.handleRoleChange(key, role)}
                  onRemove={() => picker.handleRemove(key)}
                />
              );
            })}
          </PersonList>
        </TabPanel>
        ) : (
        <TabPanel>
          {shareLinks.query.isLoading ? <ContentText>{SC.loading}</ContentText> : null}
          {linkRow ? (
            <>
              <AccessPolicySelect<ShareLinkAccess | "workspace">
                ariaLabel={SC.generalAccess}
                value={linkRow.access}
                disabled={shareLinks.put.isPending}
                onChange={(access) =>
                  access !== "workspace" &&
                  saveLink("link", { access, expires_at: linkRow.expires_at, options: linkRow.options })
                }
                options={[
                  { value: "restricted", label: SC.restricted, description: SC.restrictedHint, icon: <Lock size={16} /> },
                  { value: "workspace", label: SC.workspace, description: SC.workspaceHint, icon: <Briefcase size={16} />, comingSoon: true },
                  { value: "public", label: SC.anyone, description: SC.anyoneHint, icon: <Globe size={16} /> },
                ]}
                warning={
                  linkRow.access === "public" ? (
                    <ContentText role="note">
                      <strong>{SC.warningLead}</strong> {SC.warningRest}
                    </ContentText>
                  ) : null
                }
              />
              {linkRow.access !== "public" ? (
                <>
                  <CopyUrlField url={shareUrl} ariaLabel={SC.linkUrl} copyLabel={SC.copyLink} copiedLabel={SC.copied} copied={copied} onCopy={handleCopy} />
                </>
              ) : null}
              {linkRow.access === "public" ? (
                <>
                  <ContentText>{SC.lifetime}</ContentText>
                  <DurationPicker
                    value={linkRow.expires_at}
                    onChange={(expires_at) =>
                      saveLink("link", { access: "public", expires_at, options: linkRow.options })
                    }
                  />
                  <CopyUrlField url={publicUrl} ariaLabel={SC.linkUrl} copyLabel={SC.copyLink} copiedLabel={SC.copied} copied={copied} onCopy={handleCopy} />
                  <ShareLinkActions
                    disabled={shareLinks.put.isPending || shareLinks.reset.isPending}
                    onRevoke={() => saveLink("link", { access: "restricted", expires_at: linkRow.expires_at, options: linkRow.options })}
                    onReset={() => handleReset("link")}
                  />
                  {embedRow && !embedBlocked ? (
                    <ChangeLink type="button" onClick={() => setActiveTab("embed")}>{SC.changeInEmbed}</ChangeLink>
                  ) : null}
                </>
              ) : null}
            </>
          ) : null}
          {!linkRow && !shareLinks.query.isLoading ? <ContentText>{shareUrl}</ContentText> : null}
          {linkNote ? <ContentText>{linkNote}</ContentText> : null}
        </TabPanel>
        )} />
      )}

      {activeTab === "embed" && (
        <ContentBox>
          {embedUnavailableReason ? (
            <ContentText>{embedUnavailableReason}</ContentText>
          ) : shareLinks.query.isLoading ? (
            <ContentText>{SC.loading}</ContentText>
          ) : embedRow ? (
            <EmbedSettingsPanel
              link={embedRow}
              previewToken={previewToken.data ?? null}
              disabled={shareLinks.put.isPending}
              onChange={(body) => saveLink("embed", body)}
              onRevoke={() => saveLink("embed", { access: "restricted", expires_at: embedRow.expires_at, options: embedRow.options })}
              onReset={() => handleReset("embed")}
            />
          ) : null}
        </ContentBox>
      )}
      </TabBody>

      {apiError ? <ApiErrorBanner role="alert">{apiError}</ApiErrorBanner> : null}
    </ShareDialog>
  );
};

const initialsOf = (label: string): string =>
  label
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");

/** Person row shared by saved members and drafts: avatar, identity, role pill, remove. */
const MemberRow: React.FC<{
  testId: string;
  label: string;
  meta?: string | null;
  role: WorkspaceInviteRole;
  disabled: boolean;
  onRoleChange: (role: WorkspaceInviteRole) => void;
  onRemove: () => void;
}> = ({ testId, label, meta, role, disabled, onRoleChange, onRemove }) => (
  <PersonRow data-testid={testId}>
    <Avatar aria-hidden>{initialsOf(label)}</Avatar>
    <PersonIdentity>
      <PersonName>{label}</PersonName>
      {meta ? <PersonMeta>{meta}</PersonMeta> : null}
    </PersonIdentity>
    <RolePill>
      <WorkspaceInviteRoleSelect
        value={role}
        onChange={onRoleChange}
        disabled={disabled}
        ariaLabel={`Role for ${label}`}
        presentation="chip"
        chevronSize={14}
      />
    </RolePill>
    <RemoveButton
      type="button"
      aria-label={`Remove ${label}`}
      disabled={disabled}
      onClick={onRemove}
    >
      <CloseIcon size={16} />
    </RemoveButton>
  </PersonRow>
);

const DraftInviteRow: React.FC<{
  invite: WorkspaceMemberInvite;
  disabled: boolean;
  onRoleChange: (role: WorkspaceInviteRole) => void;
  onRemove: () => void;
}> = ({ invite, disabled, onRoleChange, onRemove }) => (
  <MemberRow
    testId={`selected-member-${getInviteKey(invite)}`}
    label={invite.kind === "user" ? invite.name : invite.email}
    meta={invite.kind === "user" ? invite.title : null}
    role={invite.role}
    disabled={disabled}
    onRoleChange={onRoleChange}
    onRemove={onRemove}
  />
);

/** Already-saved member of the story. */
const CurrentMemberRow: React.FC<{
  member: StoryCurrentMember;
  disabled: boolean;
  onRoleChange: (id: string, role: WorkspaceInviteRole) => void;
  onRemove: (id: string) => void;
}> = ({ member, disabled, onRoleChange, onRemove }) => (
  <MemberRow
    testId={`current-member-${member.id}`}
    label={member.name ?? member.email}
    meta={member.name ? member.designation ?? member.email : null}
    role={member.role}
    disabled={disabled}
    onRoleChange={(role) => onRoleChange(member.id, role)}
    onRemove={() => onRemove(member.id)}
  />
);

export default ShareStoryModal;
